import { Dialog, DialogContent, Box, Typography } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { LinearProgress } from "@mui/material";
import { Button } from "@mui/material";
import { Alert, AlertTitle } from "@mui/material";
import { ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@/store/slices/authSlice";
import {
  buildStatisticsPath,
  STATISTICS_TABS,
} from "@/pages/user/MyStatistics/constants";

const FinalScoreDialog = ({
  openExamResultDialog,
  handleCloseExamResultDialog,
  finishExamData,
  // Optional overrides so the same popup can be reused by Public Mock Tests
  // (and any future test runner) without duplicating its styling.
  title = "TOEIC Test Results",
  subtitle = "Congratulations on completing your exam!",
  headerSlot = null,
  bodySlot = null,
  actions = null,
  onClose,
  submitError,
  isSubmitting,
  onRetry,
}) => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const onCancel = () => {
    handleCloseExamResultDialog?.();
    if (onClose) onClose();
    else navigate("/");
  };

  // Guard against partial / missing /finish responses. We must not render
  // a 0/0/0 result if the API ever responds without a score (e.g. timeout
  // surfaced as success, partial payload, or after all retries failed).
  // Instead, show an explicit failure dialog with a Retry button so the
  // student is never left staring at a silent / blank screen.
  //
  // Callers that render their own score body (`bodySlot`) — e.g. part-mode
  // public mock tests, which use `partScores` instead of a scaled `score` —
  // opt out of this guard: they're responsible for their own rendering, and
  // we only treat the submit as failed if the caller actually passed a
  // `submitError`.
  const score = finishExamData?.data?.score;
  const hasScore = score !== undefined && score !== null;
  const isFailure = !!submitError || (!hasScore && !bodySlot);

  if (isFailure) {
    const status = submitError?.status;
    const isAuthError = status === 401 || status === 403;
    const message = isAuthError
      ? "Phiên đăng nhập của bạn đã hết hạn. Vui lòng đăng nhập lại rồi bấm Thử lại — câu trả lời của bạn đã được lưu trên máy chủ."
      : submitError?.data?.message ||
        "Nộp bài chưa thành công do lỗi mạng hoặc máy chủ. Câu trả lời của bạn đã được lưu — vui lòng bấm Thử lại.";

    return (
      <Dialog
        open={openExamResultDialog}
        onClose={handleCloseExamResultDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 0,
          },
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <ErrorOutlineIcon sx={{ fontSize: 56, color: "error.main", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Nộp bài thất bại
          </Typography>
          <Alert severity="warning" sx={{ textAlign: "left", mb: 3 }}>
            <AlertTitle>Không nhận được kết quả từ máy chủ</AlertTitle>
            {message}
          </Alert>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={handleCloseExamResultDialog}
              disabled={isSubmitting}
            >
              Đóng
            </Button>
            {onRetry && (
              <Button
                variant="contained"
                color="primary"
                onClick={onRetry}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {isSubmitting ? "Đang nộp lại..." : "Thử lại"}
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={openExamResultDialog}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 0,
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          boxShadow: "0 20px 60px rgba(33, 150, 243, 0.15)",
          border: "1px solid rgba(33, 150, 243, 0.1)",
        },
      }}
    >
      <DialogContent sx={{ p: 4, textAlign: "left" }}>
        {/* Account ribbon — proves which account this result belongs to. */}
        {currentUser && (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              backgroundColor: "rgba(33, 150, 243, 0.08)",
              border: "1px solid rgba(33, 150, 243, 0.2)",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#1976D2", fontWeight: 600, letterSpacing: 0.3 }}
            >
              Tài khoản:{" "}
              {currentUser.username ||
                currentUser.name ||
                [currentUser.firstName, currentUser.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                currentUser.email ||
                "—"}
              {currentUser.email &&
              (currentUser.username ||
                currentUser.name ||
                currentUser.firstName ||
                currentUser.lastName)
                ? ` · ${currentUser.email}`
                : ""}
            </Typography>
          </Box>
        )}

        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
              fontSize: { xs: "1.6rem", sm: "2rem" },
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {headerSlot}

        {bodySlot ? bodySlot : <DefaultScoreBody finishExamData={finishExamData} />}

        {/* SVG Gradient Definition (used by the circular Total score) */}
        <svg width="0" height="0">
          <defs>
            <linearGradient
              id="circularGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#2196F3" />
              <stop offset="100%" stopColor="#1976D2" />
            </linearGradient>
          </defs>
        </svg>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mt: 4,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {actions ? (
            actions({ onCancel })
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={outlinedSx}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleCloseExamResultDialog?.();
                  navigate(buildStatisticsPath(STATISTICS_TABS.EXAM));
                }}
                sx={containedSx}
              >
                View Detailed Results
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Shared button styling so callers passing custom `actions` can match.
export const outlinedSx = {
  borderRadius: 3,
  px: 3,
  py: 1,
  borderColor: "#2196F3",
  color: "#2196F3",
  "&:hover": {
    backgroundColor: "rgba(33, 150, 243, 0.04)",
    borderColor: "#1976D2",
  },
};

export const containedSx = {
  borderRadius: 3,
  px: 3,
  py: 1,
  background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
  "&:hover": {
    background: "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
  },
};

const GRADIENT_TEXT_SX = {
  fontWeight: 700,
  background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

/**
 * Single labelled progress bar used for Listening / Reading section scores.
 * `displayValue` overrides the right-hand number (useful when the bar should
 * read "1/30" while the bar fill is computed from value/max).
 */
export const ScoreBar = ({ label, value, max = 495, displayValue }) => (
  <Box sx={{ mb: 4 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: "#2c3e50", fontSize: "1.1rem" }}
      >
        {label}:
      </Typography>
      <Typography variant="h5" sx={GRADIENT_TEXT_SX}>
        {displayValue ?? value}
      </Typography>
    </Box>
    <Box sx={{ position: "relative" }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, ((value || 0) / max) * 100)}
        sx={{
          height: 12,
          borderRadius: 6,
          backgroundColor: "#e3f2fd",
          "& .MuiLinearProgress-bar": {
            borderRadius: 6,
            background: "linear-gradient(90deg, #2196F3 0%, #1976D2 100%)",
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#fff",
          border: "2px solid #1976D2",
        }}
      />
    </Box>
  </Box>
);

/**
 * Big circular "Total" badge. `displayValue` overrides the number rendered
 * inside the circle so callers can show "3%" instead of "3", while `value`
 * still drives the stroke fill.
 */
export const TotalCircle = ({
  value,
  max = 990,
  displayValue,
  label = "Total Score",
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mt: 3,
    }}
  >
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, color: "#2c3e50", fontSize: "1.2rem" }}
    >
      {label}:
    </Typography>
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={120}
        thickness={1}
        sx={{
          color: "transparent",
          "& .MuiCircularProgress-circle": {
            stroke: "url(#circularGradient)",
            strokeLinecap: "round",
          },
        }}
      />
      <CircularProgress
        variant="determinate"
        value={100}
        size={120}
        thickness={8}
        sx={{
          color: "#e3f2fd",
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: -1,
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4" component="div" sx={GRADIENT_TEXT_SX}>
          {displayValue ?? value ?? 0}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const DefaultScoreBody = ({ finishExamData }) => {
  const listening = finishExamData?.data?.listeningScore || 0;
  const reading = finishExamData?.data?.readingScore || 0;
  const total = finishExamData?.data?.score || 0;
  return (
    <>
      <ScoreBar label="Listening" value={listening} />
      <ScoreBar label="Reading" value={reading} />
      <TotalCircle value={total} />
    </>
  );
};

export default FinalScoreDialog;
